import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { Button, Spinner } from "@/components/ui/Primitives";
import { api, formatErr } from "@/lib/api";

const CATS = [
  { k: "cx1", label: "CX1" },
  { k: "cx2", label: "CX2" },
  { k: "cx3", label: "CX3" },
  { k: "cx3g", label: "CX3G" },
  { k: "cx4", label: "CX4" },
];

const TIPO_LABELS = {
  amarela: "Amarela",
  roxa: "Roxa",
  branca: "Branca",
  pelada: "Pelada",
};

const brl = (n) =>
  Number(n || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Modal para reajustar preços por categoria de uma Saída (venda).
 * Usa itens_calc pra descobrir quais (categoria, tipo_cebola) estão em uso
 * e mostra input de preço atual, permitindo digitar o novo preço.
 *
 * Props:
 *   shipmentId: string — id do shipment vinculado à AR
 *   defaultMotivo?: string
 *   onClose(): void
 *   onSaved(newValorBruto: number): void
 */
export default function PricingAdjustmentModal({ shipmentId, defaultMotivo = "", onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [ship, setShip] = useState(null);
  const [precos, setPrecos] = useState({}); // { cx1: { amarela: 0, roxa: 0, ... }, ... }
  const [motivo, setMotivo] = useState(defaultMotivo);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  // combos: [{ cat, tipo, quantidade, preco_atual }]
  const [combos, setCombos] = useState([]);
  // preview de P&L (etapa 2): { antes, depois } | null
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/shipments/${shipmentId}`);
        if (!alive) return;
        setShip(data);
        setPrecos(JSON.parse(JSON.stringify(data.precos || {})));
        // Descobrir combos em uso a partir de itens_calc/itens
        const found = new Map(); // key = `${cat}|${tipo}` -> { cat, tipo, quantidade, preco_atual }
        const items = data.itens_calc && data.itens_calc.length ? data.itens_calc : (data.itens || []);
        for (const it of items) {
          const tipo = (it.tipo_cebola || "amarela").toLowerCase();
          for (const c of CATS) {
            const q = Number((it[c.k] || {}).quantidade || 0);
            if (q <= 0) continue;
            const key = `${c.k}|${tipo}`;
            const preco = Number(((data.precos || {})[c.k] || {})[tipo] || 0);
            if (found.has(key)) {
              found.get(key).quantidade += q;
            } else {
              found.set(key, {
                cat: c.k, cat_label: c.label, tipo, tipo_label: TIPO_LABELS[tipo] || tipo,
                quantidade: q, preco_atual: preco,
              });
            }
          }
        }
        setCombos(Array.from(found.values()));
      } catch (e) {
        setErr(formatErr(e));
      } finally {
        setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [shipmentId]);

  const getPreco = (cat, tipo) => {
    const v = ((precos || {})[cat] || {})[tipo];
    return v === undefined || v === null ? 0 : v;
  };
  const setPreco = (cat, tipo, val) => {
    setPrecos((prev) => {
      const cp = JSON.parse(JSON.stringify(prev || {}));
      cp[cat] = cp[cat] || {};
      cp[cat][tipo] = val === "" ? 0 : Number(val);
      return cp;
    });
  };

  const previewSubtotal = (c) => c.quantidade * Number(getPreco(c.cat, c.tipo) || 0);
  const totalNovo = combos.reduce((s, c) => s + previewSubtotal(c), 0);
  const totalAtual = Number(ship?.valor_bruto || 0);
  const delta = totalNovo - totalAtual;

  const aplicarDescontoUniforme = (pct) => {
    if (isNaN(pct) || pct <= 0) return;
    const fator = 1 - pct / 100;
    setPrecos((prev) => {
      const cp = JSON.parse(JSON.stringify(prev || {}));
      for (const c of combos) {
        cp[c.cat] = cp[c.cat] || {};
        cp[c.cat][c.tipo] = Number((c.preco_atual * fator).toFixed(2));
      }
      return cp;
    });
  };

  const submit = async () => {
    setBusy(true);
    setErr("");
    try {
      const payload = {
        precos: precos,
        precos_splits: ship?.precos_splits || null,
        motivo: motivo || "Reajuste de preços por abatimento",
      };
      const { data } = await api.patch(`/shipments/${shipmentId}/precos`, payload);
      onSaved && onSaved(Number(data.valor_bruto || 0));
      onClose && onClose();
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  const doPreview = async () => {
    setBusy(true);
    setErr("");
    try {
      const payload = {
        precos: precos,
        precos_splits: ship?.precos_splits || null,
      };
      const { data } = await api.post(`/shipments/${shipmentId}/precos/preview`, payload);
      setPreview(data);
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="pricing-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col relative">
        <div className="p-5 border-b border-ink-200 flex items-center justify-between">
          <div>
            <h3 className="font-heading font-bold text-ink-900 text-lg">
              Reajustar preços da venda
            </h3>
            <p className="text-xs text-ink-500 mt-0.5">
              Informe os novos preços por saco em cada categoria. O sistema recalcula o valor total,
              atualiza a Conta a Receber e propaga nos relatórios.
            </p>
          </div>
          <button onClick={onClose} className="text-ink-500 hover:text-ink-900" data-testid="pricing-close">
            <X size={20} />
          </button>
        </div>

        {loading ? (
          <div className="p-10 flex justify-center"><Spinner size={24} /></div>
        ) : err && !ship ? (
          <div className="p-6 text-danger">{err}</div>
        ) : (
          <>
            <div className="p-5 overflow-y-auto flex-1">
              {/* Preset de desconto uniforme */}
              <div className="mb-4 flex items-center gap-2 flex-wrap p-3 rounded-md bg-amber-50 border border-amber-200">
                <span className="text-xs font-bold uppercase text-amber-900">Preset:</span>
                <span className="text-xs text-amber-900">Aplicar desconto uniforme de</span>
                {[5, 10, 15, 20].map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => aplicarDescontoUniforme(p)}
                    className="px-2 py-1 rounded bg-white border border-amber-300 hover:bg-amber-100 text-xs font-mono font-semibold"
                    data-testid={`preset-${p}`}
                  >
                    −{p}%
                  </button>
                ))}
                <span className="text-[10px] text-amber-800 ml-auto">Você pode editar cada linha depois</span>
              </div>

              {/* Grid de combos */}
              {combos.length === 0 ? (
                <div className="text-center text-ink-500 py-8">Nenhuma categoria com quantidade nesta venda.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-ink-50/70 text-[11px] uppercase tracking-wider text-ink-500">
                        <th className="px-3 py-2 text-left">Cat.</th>
                        <th className="px-3 py-2 text-left">Tipo</th>
                        <th className="px-3 py-2 text-right">Qtd (sc)</th>
                        <th className="px-3 py-2 text-right">Preço atual</th>
                        <th className="px-3 py-2 text-right">Novo preço</th>
                        <th className="px-3 py-2 text-right">Novo subtotal</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-ink-200">
                      {combos.map((c) => {
                        const novoPreco = Number(getPreco(c.cat, c.tipo) || 0);
                        const subtotal = c.quantidade * novoPreco;
                        const subtotalAntes = c.quantidade * c.preco_atual;
                        const diff = subtotal - subtotalAntes;
                        return (
                          <tr key={`${c.cat}|${c.tipo}`} data-testid={`row-${c.cat}-${c.tipo}`}>
                            <td className="px-3 py-2">
                              <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-brand-500/10 text-brand-700 border border-brand-500/30">
                                {c.cat_label}
                              </span>
                            </td>
                            <td className="px-3 py-2">{c.tipo_label}</td>
                            <td className="px-3 py-2 text-right font-mono">{c.quantidade}</td>
                            <td className="px-3 py-2 text-right font-mono text-ink-500">
                              R$ {brl(c.preco_atual)}
                            </td>
                            <td className="px-3 py-2 text-right">
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={novoPreco}
                                onChange={(e) => setPreco(c.cat, c.tipo, e.target.value)}
                                className="w-24 text-right font-mono border border-brand-500 rounded px-2 py-1 text-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500"
                                data-testid={`price-${c.cat}-${c.tipo}`}
                              />
                            </td>
                            <td className="px-3 py-2 text-right font-mono font-semibold">
                              R$ {brl(subtotal)}
                              {Math.abs(diff) > 0.005 && (
                                <div className={`text-[10px] font-normal ${diff < 0 ? "text-red-700" : "text-emerald-700"}`}>
                                  {diff > 0 ? "+" : ""}R$ {brl(diff)}
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="bg-ink-50 border-t-2 border-ink-300 font-bold">
                        <td className="px-3 py-2" colSpan={3}>Total ({combos.length} categorias)</td>
                        <td className="px-3 py-2 text-right font-mono text-ink-500">
                          R$ {brl(totalAtual)}
                        </td>
                        <td></td>
                        <td className="px-3 py-2 text-right font-mono">
                          R$ {brl(totalNovo)}
                          {Math.abs(delta) > 0.005 && (
                            <div className={`text-[10px] font-normal ${delta < 0 ? "text-red-700" : "text-emerald-700"}`}>
                              {delta > 0 ? "+" : ""}R$ {brl(delta)}
                            </div>
                          )}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}

              <div className="mt-4">
                <label className="text-[10px] uppercase tracking-wider text-ink-500 font-bold block mb-1">
                  Motivo (registrado no histórico da saída)
                </label>
                <input
                  type="text"
                  value={motivo}
                  onChange={(e) => setMotivo(e.target.value)}
                  placeholder="Ex.: Cliente reclamou de qualidade — 5% de abatimento acordado"
                  className="w-full rounded-md border border-ink-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                  data-testid="pricing-motivo"
                />
              </div>

              {err && (
                <div className="mt-3 text-sm text-danger bg-red-50 border border-red-200 px-3 py-2 rounded-md">
                  {err}
                </div>
              )}
            </div>
            <div className="p-4 border-t border-ink-200 bg-ink-50/40 flex items-center justify-between">
              <div className="text-sm text-ink-500">
                {Math.abs(delta) > 0.005 ? (
                  <>Novo total: <span className={`font-mono font-bold ${delta < 0 ? "text-red-700" : "text-emerald-700"}`}>R$ {brl(totalNovo)}</span> ({delta > 0 ? "+" : ""}R$ {brl(delta)})</>
                ) : (
                  <>Sem alteração no valor total</>
                )}
              </div>
              <div className="flex gap-2">
                <Button variant="secondary" onClick={onClose} data-testid="pricing-cancel">Cancelar</Button>
                <Button onClick={doPreview} disabled={busy || combos.length === 0} data-testid="pricing-preview">
                  {busy ? <Spinner size={16} /> : "Ver lucro/prejuízo →"}
                </Button>
              </div>
            </div>
          </>
        )}

        {/* Etapa 2: Confirmação com P&L (antes vs depois) */}
        {preview && (
          <PnlConfirmScreen
            preview={preview}
            motivo={motivo}
            busy={busy}
            err={err}
            onBack={() => setPreview(null)}
            onConfirm={submit}
          />
        )}
      </div>
    </div>
  );
}


function PnlConfirmScreen({ preview, motivo, busy, err, onBack, onConfirm }) {
  const a = preview.antes || {};
  const d = preview.depois || {};
  const dReceita = Number(d.receita_bruta || 0) - Number(a.receita_bruta || 0);
  const dLucro = Number(d.lucro || 0) - Number(a.lucro || 0);
  const dMargem = Number(d.margem_pct || 0) - Number(a.margem_pct || 0);
  const isPrejuizo = Number(d.lucro || 0) < 0;

  return (
    <div className="absolute inset-0 bg-white flex flex-col" data-testid="pnl-confirm-screen">
      <div className="p-5 border-b border-ink-200">
        <h3 className="font-heading font-bold text-ink-900 text-lg">
          Confira o resultado antes de aplicar
        </h3>
        <p className="text-xs text-ink-500 mt-0.5">
          Comparativo do lucro atual vs. lucro com os novos preços. O CMV (custo) permanece igual.
        </p>
      </div>

      <div className="p-5 overflow-y-auto flex-1 space-y-4">
        {/* Big result: Lucro depois */}
        <div className={`p-5 rounded-lg border-2 ${
          isPrejuizo ? "bg-red-50 border-red-300" : "bg-emerald-50 border-emerald-300"
        }`}>
          <div className={`text-xs uppercase tracking-wider font-bold ${
            isPrejuizo ? "text-red-700" : "text-emerald-700"
          }`}>
            {isPrejuizo ? "⚠ PREJUÍZO com os novos preços" : "Lucro com os novos preços"}
          </div>
          <div className={`text-4xl font-mono font-bold mt-1 ${
            isPrejuizo ? "text-red-800" : "text-emerald-800"
          }`} data-testid="pnl-lucro-depois">
            R$ {brl(d.lucro || 0)}
          </div>
          <div className={`text-sm mt-1 ${
            isPrejuizo ? "text-red-700" : "text-emerald-700"
          }`}>
            Margem: <strong>{Number(d.margem_pct || 0).toFixed(2)}%</strong>
            <span className="text-ink-500 ml-2">
              ({dMargem >= 0 ? "+" : ""}{dMargem.toFixed(2)} p.p. vs atual)
            </span>
          </div>
        </div>

        {/* Comparativo em cards */}
        <div className="grid grid-cols-2 gap-3">
          <div className="p-4 rounded-lg border border-ink-200 bg-ink-50/30">
            <div className="text-[10px] uppercase tracking-wider font-bold text-ink-500 mb-2">
              Situação ATUAL
            </div>
            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between">
                <span className="text-ink-600">Receita bruta</span>
                <span className="font-mono">R$ {brl(a.receita_bruta)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-ink-600">(-) Deduções</span>
                <span className="font-mono text-ink-500">R$ {brl(a.deducoes)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-ink-600">(-) CMV</span>
                <span className="font-mono text-ink-500">R$ {brl(a.cmv)}</span>
              </div>
              <div className="flex justify-between pt-2 border-t border-ink-200 mt-2">
                <span className="font-bold">Lucro</span>
                <span className="font-mono font-bold">R$ {brl(a.lucro)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-ink-600">Margem</span>
                <span className="font-mono">{Number(a.margem_pct || 0).toFixed(2)}%</span>
              </div>
            </div>
          </div>

          <div className={`p-4 rounded-lg border-2 ${
            isPrejuizo ? "border-red-300 bg-red-50/40" : "border-brand-500 bg-brand-500/5"
          }`}>
            <div className={`text-[10px] uppercase tracking-wider font-bold mb-2 ${
              isPrejuizo ? "text-red-700" : "text-brand-700"
            }`}>
              Com os novos preços
            </div>
            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between">
                <span className="text-ink-600">Receita bruta</span>
                <span className="font-mono">R$ {brl(d.receita_bruta)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-ink-600">(-) Deduções</span>
                <span className="font-mono text-ink-500">R$ {brl(d.deducoes)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-ink-600">(-) CMV</span>
                <span className="font-mono text-ink-500">R$ {brl(d.cmv)}</span>
              </div>
              <div className="flex justify-between pt-2 border-t border-ink-200 mt-2">
                <span className="font-bold">Lucro</span>
                <span className={`font-mono font-bold ${isPrejuizo ? "text-red-700" : ""}`}>
                  R$ {brl(d.lucro)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-ink-600">Margem</span>
                <span className="font-mono">{Number(d.margem_pct || 0).toFixed(2)}%</span>
              </div>
            </div>
          </div>
        </div>

        {/* Delta */}
        <div className={`p-3 rounded-md border ${
          dLucro < 0 ? "bg-red-50 border-red-200 text-red-800" : "bg-emerald-50 border-emerald-200 text-emerald-800"
        } flex items-center justify-between`}>
          <div className="text-xs font-bold">Impacto do ajuste</div>
          <div className="flex items-center gap-4 text-sm font-mono">
            <span>Receita {dReceita >= 0 ? "+" : ""}R$ {brl(dReceita)}</span>
            <span className="font-bold">
              Lucro {dLucro >= 0 ? "+" : ""}R$ {brl(dLucro)}
            </span>
          </div>
        </div>

        {/* Breakdown por categoria */}
        {(d.categorias || []).length > 0 && (
          <div className="overflow-x-auto border border-ink-200 rounded-md">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-ink-50/70 text-[10px] uppercase tracking-wider text-ink-500">
                  <th className="px-2 py-2 text-left">Cat.</th>
                  <th className="px-2 py-2 text-right">Qtd</th>
                  <th className="px-2 py-2 text-right">Preço venda</th>
                  <th className="px-2 py-2 text-right">Preço custo</th>
                  <th className="px-2 py-2 text-right">Receita</th>
                  <th className="px-2 py-2 text-right">CMV</th>
                  <th className="px-2 py-2 text-right">Lucro</th>
                  <th className="px-2 py-2 text-right">Margem</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {(d.categorias || []).filter((c) => Number(c.quantidade || 0) > 0).map((c) => (
                  <tr key={c.categoria}>
                    <td className="px-2 py-1.5 font-bold">{c.categoria}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{c.quantidade}</td>
                    <td className="px-2 py-1.5 text-right font-mono">R$ {brl(c.preco_venda_medio)}</td>
                    <td className="px-2 py-1.5 text-right font-mono text-ink-500">R$ {brl(c.preco_custo_medio)}</td>
                    <td className="px-2 py-1.5 text-right font-mono">R$ {brl(c.receita)}</td>
                    <td className="px-2 py-1.5 text-right font-mono text-ink-500">R$ {brl(c.cmv)}</td>
                    <td className={`px-2 py-1.5 text-right font-mono font-semibold ${
                      Number(c.lucro || 0) < 0 ? "text-red-700" : ""
                    }`}>
                      R$ {brl(c.lucro)}
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono">
                      {Number(c.margem_pct || 0).toFixed(1)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {motivo && (
          <div className="text-xs text-ink-500 border-t border-ink-100 pt-3">
            <strong>Motivo:</strong> {motivo}
          </div>
        )}

        {err && (
          <div className="text-sm text-danger bg-red-50 border border-red-200 px-3 py-2 rounded-md">
            {err}
          </div>
        )}
      </div>

      <div className="p-4 border-t border-ink-200 bg-ink-50/40 flex justify-between">
        <Button variant="secondary" onClick={onBack} data-testid="pnl-back">
          ← Voltar e editar
        </Button>
        <Button onClick={onConfirm} disabled={busy} data-testid="pnl-confirm">
          {busy ? <Spinner size={16} /> : "Confirmar e aplicar novos preços"}
        </Button>
      </div>
    </div>
  );
}
